<template>
  <div class="sr-root">
    <div class="sr-main">
      <div id="payto-form" class="sr-payment-form">
        <div class="sr-combo-inputs-row">
          <div class="col">
            <label for="payto-name">Account Holder Name</label>
            <input
              v-validate="'required'"
              type="text"
              id="payto-name"
              name="accountHolderName"
              placeholder="John Smith"
              :danger="errors.first('accountHolderName') ? true : false"
              :success="!errors.first('accountHolderName') && payToDetails.accountHolderName != ''"
              data-vv-validate-on="blur"
              data-vv-as="Account Holder Name"
              v-model="payToDetails.accountHolderName"
            />
            <span class="text-danger text-sm">{{ errors.first('accountHolderName') }}</span>
          </div>
        </div>

        <div class="sr-combo-inputs-row">
          <div class="col">
            <a class="payto-toggle" @click="usePayId = !usePayId">
              {{ usePayId ? 'Use BSB and account number instead' : 'Use PayID instead' }}
            </a>
          </div>
        </div>

        <div class="sr-combo-inputs-row" v-if="usePayId" key="payto-payid-row">
          <div class="col">
            <label for="payto-payid">PayID (email or phone number)</label>
            <input
              v-validate="'required'"
              type="text"
              id="payto-payid"
              name="payId"
              placeholder="jenny@example.com"
              data-vv-validate-on="blur"
              data-vv-as="PayID"
              v-model="payToDetails.payId"
            />
            <span class="text-danger text-sm">{{ errors.first('payId') }}</span>
          </div>
        </div>
        <div class="sr-combo-inputs-row" v-else key="payto-bsb-row">
          <div class="col">
            <label for="payto-bsb">BSB</label>
            <input
              v-validate="'required|regex:^\\d{3}-?\\d{3}$'"
              type="text"
              id="payto-bsb"
              name="bsbNumber"
              placeholder="000-000"
              data-vv-validate-on="blur"
              data-vv-as="BSB"
              v-model="payToDetails.bsbNumber"
            />
            <span class="text-danger text-sm">{{ errors.first('bsbNumber') }}</span>
          </div>
          <div class="col">
            <label for="payto-account">Account Number</label>
            <input
              v-validate="{ required: true, regex: /^\d{4,10}$/ }"
              type="text"
              id="payto-account"
              name="accountNumber"
              placeholder="000123456"
              data-vv-validate-on="blur"
              data-vv-as="Account Number"
              v-model="payToDetails.accountNumber"
            />
            <span class="text-danger text-sm">{{ errors.first('accountNumber') }}</span>
          </div>
        </div>

        <!-- Email is required for PayID payments, for refund processing; on
        the BSB path it falls back to the clinic's stored paymentEmail, which
        is frequently blank, so it is validated here too rather than only
        when the PayID tab is showing. -->
        <div class="sr-combo-inputs-row">
          <div class="col">
            <label for="payto-email">Email Address</label>
            <input
              v-validate="'required|email'"
              type="email"
              id="payto-email"
              name="email"
              placeholder="jenny@example.com"
              data-vv-validate-on="blur"
              v-model="payToDetails.email"
            />
            <span class="text-danger text-sm">{{ errors.first('email') }}</span>
          </div>
        </div>

        <div id="payto-error-message" role="alert"></div>

        <!--
          FRH-734: the maximum amount ($25,000) is confirmed by product and
          shown here as text rather than fetched from the server. If the
          configurable PAYTO_MAX_MANDATE_AMOUNT_CENTS override is ever
          changed, this copy needs updating too — it will not pick up a
          changed limit automatically.
        -->
        <div class="col" id="payto-agreement">
          By providing bank account details and confirming this payment, you
          agree to authorise Fresh Clinics to debit this account via PayTo for
          future Store orders, up to a maximum of $25,000 AUD per payment,
          until the agreement is cancelled here or through the bank. This
          PayTo transaction may be subject to the terms and conditions of the
          relevant financial service provider.
        </div>

        <button :disabled="isDisabled" @click="confirmPayToDetails">
          <span v-if="!isDisabled">{{ buttonTitle ? buttonTitle : 'Confirm' }}</span>
          <span v-else>Confirming&hellip;</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';

  export default {
    name: 'PayToSetup',
    props: {
      clinicId: {
        type: String,
        required: true,
      },
      paymentEmail: {
        type: String,
        default: '',
      },
      buttonTitle: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        stripe: null,
        isDisabled: false,
        usePayId: false,
        payToDetails: {
          accountHolderName: '',
          email: this.paymentEmail || '',
          payId: '',
          bsbNumber: '',
          accountNumber: '',
        },
      };
    },
    methods: {
      ...mapActions('clinic', [
        'getStripePublishableKey',
        'createPayToSetupIntent',
        'removePayTo',
      ]),
      getPublishableKey() {
        this.$vs.loading();
        this.getStripePublishableKey()
          .then((res) => {
            // eslint-disable-next-line no-undef
            this.stripe = Stripe(res.data.publishableKey);
            this.$vs.loading.close();
          })
          .catch(() => {
            this.$vs.notify({
              title: 'Error',
              text: 'Oops ! something went wrong. Please try later.',
              color: 'danger',
            });
            this.$vs.loading.close();
          });
      },
      showError(errorMsgText) {
        this.isDisabled = false;
        const errorMsg = document.querySelector('#payto-error-message');
        if (errorMsg) errorMsg.textContent = errorMsgText;
        this.$vs.notify({
          title: 'Error',
          text: errorMsgText,
          color: 'danger',
        });
      },
      async confirmPayToDetails() {
        const isValid = await this.$validator.validateAll();
        if (!isValid) return;

        // getPublishableKey()'s failure path only notifies and never sets
        // this.stripe — without this guard, createPayToSetupIntent below
        // would still run (marking the clinic Pending server-side) before
        // failing on a null this.stripe with no rollback.
        if (!this.stripe) {
          this.showError('Oops ! something went wrong. Please try later.');
          return;
        }

        this.isDisabled = true;
        try {
          // Account holder name / PayID / BSB / account number are never
          // sent here — they go straight from the browser to Stripe via
          // stripe.confirmPayToSetup below. The masked display detail shown
          // later in Payment Settings/Checkout comes from Stripe's own
          // resolved PaymentMethod, filled in server-side once the setup
          // succeeds (see the paired web-api MR's webhook handler).
          const created = await this.createPayToSetupIntent({ id: this.clinicId });
          const { clientSecret, accountId } = created.data.data;

          const paymentMethod = {
            billing_details: {
              name: this.payToDetails.accountHolderName,
              email: this.payToDetails.email,
            },
            payto: this.usePayId
              ? { pay_id: this.payToDetails.payId }
              : {
                  account_number: this.payToDetails.accountNumber,
                  bsb_number: this.payToDetails.bsbNumber,
                },
          };

          const result = await this.stripe.confirmPayToSetup(clientSecret, {
            payment_method: paymentMethod,
          });

          if (result.error) {
            // createPayToSetupIntent already pushed this account as Pending
            // server-side before this local Stripe.js validation ran, and no
            // webhook will ever resolve a mandate that was never created at
            // Stripe. FRH-749: every attempt is its own independent account
            // now, so removal is always safe here — it can never destroy a
            // different, already-working mandate as collateral.
            try {
              await this.removePayTo({ id: this.clinicId, accountId });
            } catch (rollbackError) {
              // Surfaced to the user below regardless; nothing else to do
              // with a failed rollback here.
            }
            this.showError(result.error.message);
            this.$emit('payto-setup-failed');
            return;
          }

          this.$emit('payto-setup-success');
        } catch (error) {
          this.showError('Oops ! something went wrong. Please try later.');
          this.$emit('error', error);
        } finally {
          this.isDisabled = false;
        }
      },
    },
    created() {
      this.getPublishableKey();
    },
  };
</script>

<style scoped>
  * {
    box-sizing: border-box;
  }
  .sr-root {
    width: 100%;
  }
  .sr-main {
    width: 100%;
    padding: 24px;
    background: rgb(247, 250, 252);
    border-radius: 6px;
  }
  input {
    height: 40px;
    padding: 10px 12px;
    color: #32325d;
    background-color: white;
    border: 1px solid transparent;
    border-radius: 4px;
    box-shadow: 0 1px 3px 0 #e6ebf1;
    width: 100%;
    outline: none;
    font-size: 0.875rem !important;
  }
  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #6b7c93;
  }
  .sr-combo-inputs-row {
    display: flex;
    width: 100%;
    margin-top: 20px;
  }
  .sr-combo-inputs-row:first-child {
    margin-top: 0;
  }
  .sr-combo-inputs-row .col:not(:last-child) {
    margin-right: 20px;
  }
  .sr-combo-inputs-row .col {
    width: 100%;
  }
  .payto-toggle {
    color: rgb(107, 124, 147);
    text-decoration: underline;
    cursor: pointer;
  }
  #payto-agreement {
    margin-top: 8px;
    font-size: 14px;
    text-align: justify;
  }
  #payto-error-message {
    margin-top: 12px;
    color: rgba(var(--vs-danger), 1) !important;
    font-size: 0.875rem !important;
    min-height: 20px;
  }
  button {
    background: rgb(50, 50, 93);
    border-radius: 6px;
    color: white;
    border: 0;
    padding: 12px 16px;
    margin-top: 16px;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
